import React, { memo, useMemo, useState, useEffect } from 'react';
import { Box, Divider, Skeleton } from '@mui/material';
import classNames from 'classnames';
import { flatten, max } from 'lodash';

import ErrorBoundary from 'components/ErrorBoundary';
import GraphTimeline from 'components/GraphTimeline';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { Swell, SwellsData, SwellsTimestep } from 'types/swell';
import type { WaveEnergyGraphData, WaveEnergyTimestep } from 'types/waveEnergy';
import nextHighest from 'utils/nextHighest';
import { hasDisplayableWaveEnergyForDayIndices } from 'utils/waveEnergyTableDisplay';
import ErrorMessage from '../ErrorMessage';
import DayNightShading from '../DayNightShading';
import ForecastGraphEnergyContainer from './components/ForecastGraphEnergyContainer';

import styles from './ForecastGraphEnergy.module.scss';

const EMPTY_SWELLS: Swell[] = [];

interface Props {
  daysToDisplay: Array<number>;
  sunlightTimes: SunlightTimes;
  swells: SwellsData;
  waveEnergy?: WaveEnergyGraphData;
}

function mapWaveEnergyToSwellsTimesteps(steps: WaveEnergyTimestep[]): SwellsTimestep[] {
  return steps.map((step) => ({
    power: step.nearshore,
    swells: EMPTY_SWELLS,
    timestamp: step.timestamp,
    utcOffset: step.utcOffset,
  }));
}

export const ForecastGraphEnergyContent: React.FC<Props> = ({
  daysToDisplay,
  sunlightTimes,
  swells,
  waveEnergy,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const flatWaveEnergySteps = useMemo(() => {
    if (waveEnergy === undefined || waveEnergy.loading || waveEnergy.error || !waveEnergy.days) {
      return null;
    }
    const flat = daysToDisplay.flatMap((day) => waveEnergy.days?.[day] ?? []);
    return flat.length ? (flat as WaveEnergyTimestep[]) : null;
  }, [waveEnergy, daysToDisplay]);

  const usesNearshoreFromWaveEnergy = flatWaveEnergySteps !== null;

  const plotData = useMemo((): SwellsTimestep[] => {
    if (usesNearshoreFromWaveEnergy && flatWaveEnergySteps) {
      return mapWaveEnergyToSwellsTimesteps(flatWaveEnergySteps);
    }
    return (
      daysToDisplay
        ?.map((day) => swells.days?.[day])
        ?.filter((day) => day)
        ?.flat() ?? []
    );
  }, [usesNearshoreFromWaveEnergy, flatWaveEnergySteps, daysToDisplay, swells.days]);

  const overallMaxEnergy = useMemo(() => {
    const values = usesNearshoreFromWaveEnergy
      ? (flatten(waveEnergy!.days) as WaveEnergyTimestep[]).map((s) => s.nearshore)
      : (flatten(swells.days) as SwellsTimestep[]).map((item) => item.power);
    const maxEnergy = max(values) || 0;
    if (maxEnergy <= 300) return 300;
    if (maxEnergy > 300 && maxEnergy <= 1000) return nextHighest(maxEnergy, 100);
    if (maxEnergy > 1000 && maxEnergy <= 5000) return nextHighest(maxEnergy, 500);
    if (maxEnergy > 5000) return nextHighest(maxEnergy, 1000);
    return maxEnergy;
  }, [usesNearshoreFromWaveEnergy, waveEnergy?.days, swells.days]);

  const timeElements = useMemo(
    () =>
      daysToDisplay.map((day: number, dayIndex) => (
        <Box key={day} width="100%" display="flex">
          <Divider
            className={classNames(dayIndex !== 0 && styles.divider)}
            orientation="vertical"
            flexItem
            sx={{ border: 'none' }}
          />
          <div className={classNames(styles.dayWrapper)} key={daysToDisplay[dayIndex]}>
            <div
              className={classNames([styles.dayNightShading, 'day-night-shading'])}
              data-testid="day-night-shading-day"
            >
              <DayNightShading
                key={daysToDisplay[dayIndex]}
                sunlightTimes={sunlightTimes}
                numDays={daysToDisplay.length}
                forecastDayIndex={day}
                columnIndex={dayIndex}
                isPaywalled={false}
              />
            </div>
            {(usesNearshoreFromWaveEnergy ? waveEnergy?.days?.[day] : swells.days?.[day])
              ?.length ? (
              <GraphTimeline
                day={
                  (usesNearshoreFromWaveEnergy
                    ? waveEnergy?.days?.[day]
                    : swells.days?.[day]) as any
                }
                showMarkers
              />
            ) : null}
          </div>
        </Box>
      )),
    [daysToDisplay, sunlightTimes, swells.days, usesNearshoreFromWaveEnergy, waveEnergy?.days],
  );

  const showLoading =
    !isMounted ||
    (waveEnergy !== undefined && waveEnergy.loading) ||
    (!usesNearshoreFromWaveEnergy && swells.loading);

  return showLoading ? (
    <Skeleton className={styles.loading} data-testid="energy-graph-loading" variant="rectangular" />
  ) : (
    <div className={styles.wrapper} data-testid="energy-graph">
      <div className={styles.content}>
        <ForecastGraphEnergyContainer
          data={plotData}
          numberOfTicks={5}
          overallMaxEnergy={overallMaxEnergy}
        />
        {timeElements}
      </div>
    </div>
  );
};

const ForecastGraphEnergy: React.FC<Props> = (props) => {
  const { swells, waveEnergy, daysToDisplay } = props;
  const usesNearshoreFromWaveEnergyPlot = hasDisplayableWaveEnergyForDayIndices(
    waveEnergy,
    daysToDisplay,
  );
  const chartError = usesNearshoreFromWaveEnergyPlot
    ? (waveEnergy?.error ?? null)
    : waveEnergy?.error || swells.error;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: chartError });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-energy"
      render={() => (
        <Box className={styles.error} data-testid="energy-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <ForecastGraphEnergyContent {...props} />
    </ErrorBoundary>
  );
};

export default memo(ForecastGraphEnergy);
